import { useState } from "react";

import { type AirHandlerUnit, type Floor } from "@eisox/api/core-api";
import { cn, Plan, Select } from "@eisox/design-system";
import { ShiftIcon } from "@eisox/icons";
import { useMediaQuery } from "@eisox/tools";
import { ParaglideMessage } from "@inlang/paraglide-js-react";
import { useQuery } from "@tanstack/react-query";

import { ExpandedAirHandlerUnit } from "~/components/expended-air-handler-unit";
import ExpandedRoom from "~/components/expended-room";
import { FieldContainer } from "~/components/field-container";
import { floorPlanQueries } from "~/features/floor/floor-plan.queries";
import { m } from "~/paraglide/messages";
import type { EnhancedRoom } from "~/types/room.type";
import { toggleIdsInSelection } from "~/utils/plan-selection";

interface GroupPlanEditorProps {
  floors: Floor[];
  rooms?: EnhancedRoom[];
  airHandlerUnits?: AirHandlerUnit[];
  selectedItems: string[];
  onChange: (selectedItems: string[]) => void;
  itemHovered?: string;
  disabled?: boolean;
  currentGroupIndex?: number | null;
  itemFields?: { name: string; items: string[] }[];
}

export const GroupPlanEditor: React.FC<GroupPlanEditorProps> = ({
  floors,
  rooms,
  airHandlerUnits,
  selectedItems,
  onChange,
  itemHovered,
  disabled = false,
  currentGroupIndex,
  itemFields,
}) => {
  const isDesktop = useMediaQuery("(min-width: 1200px)");

  const [floor, setFloor] = useState(floors[0]);

  const { data: imgUrl } = useQuery({
    ...floorPlanQueries.byId(floor?.id ?? ""),
    enabled: !!floor?.id,
    staleTime: 0,
  });

  if (!floor) return null;

  const handleChangePlan = (value: string) => {
    const newFloor = floors.find(f => f.id === value);
    if (newFloor) setFloor(newFloor);
  };

  const isAhuDisabled = (r: AirHandlerUnit): boolean => {
    const currentField = currentGroupIndex != null ? itemFields?.[currentGroupIndex] : undefined;
    return Boolean(
      disabled ||
      (itemFields?.some(f => f.items.includes(r.id)) &&
        currentGroupIndex != undefined &&
        currentGroupIndex < itemFields.length &&
        currentField != null &&
        !currentField.items.includes(r.id)),
    );
  };

  const airHandlerUnitsWithPlanDefined = (
    airHandlerUnit: AirHandlerUnit,
  ): airHandlerUnit is AirHandlerUnit & { floor: { id: string; x: number; y: number } } =>
    airHandlerUnit.floor !== undefined;

  const handleSelectItem = (item: EnhancedRoom | AirHandlerUnit) => {
    if (selectedItems.includes(item.id)) {
      onChange(selectedItems.filter(id => id !== item.id));
    } else {
      onChange([...selectedItems, item.id]);
    }
  };

  const isOnCurrentFloor = (id: string) => {
    const room = rooms?.find(r => r.id === id);
    if (room) return room.floor.id === floor.id;
    const ahu = airHandlerUnits?.find(a => a.id === id);
    return ahu?.floor?.id === floor.id;
  };

  const handleRectangleSelect = (ids: string[]) => {
    const selectableIds = ids.filter(id => {
      const room = rooms?.find(r => r.id === id);
      if (room) return !disabled;
      const ahu = airHandlerUnits?.find(a => a.id === id);
      if (ahu) return !isAhuDisabled(ahu);
      return false;
    });

    const otherFloorSelections = selectedItems.filter(id => !isOnCurrentFloor(id));
    const currentFloorSelections = selectedItems.filter(isOnCurrentFloor);
    const toggled = toggleIdsInSelection(currentFloorSelections, selectableIds);
    onChange([...otherFloorSelections, ...toggled]);
  };

  return (
    <div className="relative flex w-full flex-1 flex-col gap-[20px] overflow-hidden">
      <div className="flex items-end justify-between">
        <FieldContainer label={m.level()}>
          <Select.Root multiple={false} value={floor.id} onValueChange={handleChangePlan}>
            <Select.Trigger className="w-[300px]">
              <Select.Value placeholder={m.level()} />
            </Select.Trigger>
            <Select.Content>
              <Select.Viewport>
                {floors.map(f => {
                  return (
                    <Select.Item key={f.id} value={f.id}>
                      {f.name}
                    </Select.Item>
                  );
                })}
              </Select.Viewport>
            </Select.Content>
          </Select.Root>
        </FieldContainer>
        {isDesktop && (
          <p className="mb-0 font-medium text-right text-base leading-[24px]">
            <ParaglideMessage
              message={m.group_plan_instructions}
              markup={{
                i: () => (
                  <span
                    style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
                    className="rounded-[6px] bg-primary-gray px-[8px] py-[2px]"
                  >
                    <ShiftIcon style={{ width: 12, height: 12 }} />
                    MAJ
                  </span>
                ),
              }}
            />
          </p>
        )}
      </div>
      <Plan.Root rectangleSelection onRectangleSelect={handleRectangleSelect}>
        <Plan.Wrapper className="h-full! w-full!" plan={imgUrl}>
          {rooms
            ?.filter(r => r.floor.id === floor.id)
            .map(r => (
              <Plan.Item
                key={r.id}
                selectionId={r.id}
                selectionDisabled={disabled}
                position={{ x: r.floor.x, y: r.floor.y }}
              >
                <ExpandedRoom
                  selected={selectedItems.includes(r.id)}
                  className={cn("transition-transform duration-500", itemHovered === r.id && "scale-150")}
                  disabled={disabled}
                  onClick={() => !disabled && handleSelectItem(r)}
                >
                  {r.name}
                </ExpandedRoom>
              </Plan.Item>
            ))}
          {airHandlerUnits
            ?.filter(airHandlerUnitsWithPlanDefined)
            .filter(ahu => ahu.floor.id === floor.id)
            .map(ahu => (
              <Plan.Item
                key={ahu.id}
                selectionId={ahu.id}
                selectionDisabled={isAhuDisabled(ahu)}
                defaultPosition={{ x: ahu.floor.x, y: ahu.floor.y }}
              >
                <ExpandedAirHandlerUnit
                  selected={selectedItems.includes(ahu.id)}
                  className={cn("transition-transform duration-500", itemHovered === ahu.id && "scale-150")}
                  disabled={isAhuDisabled(ahu)}
                  onClick={() => !isAhuDisabled(ahu) && handleSelectItem(ahu)}
                >
                  {ahu.name}
                </ExpandedAirHandlerUnit>
              </Plan.Item>
            ))}
        </Plan.Wrapper>
      </Plan.Root>
    </div>
  );
};
